import React, { useState, useContext, useEffect, useCallback } from 'react';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { useDialog } from '../components/ConfirmProvider';
import {
  FaInfoCircle, FaPlus, FaTrash, FaEdit, FaShieldAlt,
  FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';

const ENTRY_TYPES = [
  { v: 'name',         label: 'Person / entity name' },
  { v: 'counterparty', label: 'Counterparty name' },
  { v: 'id_number',    label: 'National ID' },
  { v: 'passport',     label: 'Passport number' },
  { v: 'phone',        label: 'Phone number' },
  { v: 'email',        label: 'Email address' },
  { v: 'account',      label: 'Account / external reference' }
];
const SEVERITIES = ['low','medium','high','critical'];

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const blankEntry = () => ({
  entry_type: 'name', value: '', severity: 'high', reason: '', notes: '', expires_at: ''
});

function ManageWatchlist() {
  const { user } = useContext(UserContext);
  const { confirm } = useDialog();
  const canRead   = hasPermission(user, 'org_watchlist.read');
  const canCreate = hasPermission(user, 'org_watchlist.create');
  const canUpdate = hasPermission(user, 'org_watchlist.update');
  const canToggle = hasPermission(user, 'org_watchlist.toggle');
  const canDelete = hasPermission(user, 'org_watchlist.delete');

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ entry_type: 'all', active: 'all', query: '' });
  const [editor, setEditor] = useState(null);   // { mode, existingId?, data }
  const [busy, setBusy] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get('/org-watchlist/stats'); setStats(r.data); }
    catch (_) { setStats(null); }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      if (filter.entry_type !== 'all') p.set('entry_type', filter.entry_type);
      if (filter.active !== 'all')     p.set('active', filter.active);
      if (filter.query.trim())         p.set('query', filter.query.trim());
      p.set('limit', '500');
      const r = await axios.get(`/org-watchlist?${p.toString()}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    fetchRows();
  }, [canRead, fetchStats, fetchRows]);

  const openCreate = () => setEditor({ mode: 'create', data: blankEntry() });
  const openEdit = (row) => setEditor({
    mode: 'edit',
    existingId: row.id,
    data: {
      entry_type: row.entry_type,   // immutable in edit mode
      value:      row.value,
      severity:   row.severity,
      reason:     row.reason || '',
      notes:      row.notes || '',
      expires_at: row.expires_at ? String(row.expires_at).slice(0, 10) : ''
    }
  });

  const submit = async () => {
    if (!editor?.data?.value?.trim()) {
      setError('Value is required');
      return;
    }
    setBusy(true); setError(null);
    try {
      const payload = {
        value: editor.data.value.trim(),
        severity: editor.data.severity,
        reason: editor.data.reason.trim() || undefined,
        notes: editor.data.notes.trim() || undefined,
        expires_at: editor.data.expires_at || undefined
      };
      if (editor.mode === 'create') {
        payload.entry_type = editor.data.entry_type;
        await axios.post('/org-watchlist', payload);
      } else {
        await axios.patch(`/org-watchlist/${editor.existingId}`, payload);
      }
      setEditor(null);
      fetchRows(); fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (row) => {
    try {
      await axios.post(`/org-watchlist/${row.id}/toggle`);
      fetchRows(); fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Toggle failed');
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: 'Remove watchlist entry',
      message: `Remove "${row.value}" from the watchlist? This cannot be undone.`,
      confirmLabel: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/org-watchlist/${row.id}`);
      fetchRows(); fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Delete failed');
    }
  };

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view the watchlist.
            Ask your administrator for the <code>org_watchlist.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Stats */}
      <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
        <div className="stats-grid">
          <div className="stat-card stat-card--teal">
            <div className="stat-label">Total entries</div>
            <div className="stat-value stat-value--teal">{stats ? stats.total : '—'}</div>
          </div>
          <div className="stat-card stat-card--blue">
            <div className="stat-label">Active</div>
            <div className="stat-value stat-value--blue">{stats ? stats.active : '—'}</div>
          </div>
          <div className="stat-card stat-card--orange">
            <div className="stat-label">Inactive</div>
            <div className="stat-value stat-value--orange">
              {stats ? (stats.total - stats.active) : '—'}
            </div>
          </div>
        </div>
      </div>
        </div>

      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">Internal watchlist</h4>
          <p className="form-section__subtitle">
            Names, IDs, phones, emails, and account references your organisation has blocked or wants flagged on every screening run.
          </p>
        </header>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Type</label>
            <select value={filter.entry_type}
                    onChange={e => setFilter(f => ({ ...f, entry_type: e.target.value }))}>
              <option value="all">All types</option>
              {ENTRY_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Active</label>
            <select value={filter.active}
                    onChange={e => setFilter(f => ({ ...f, active: e.target.value }))}>
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="form-group">
            <label>Search</label>
            <input type="text" placeholder="value / reason"
                   value={filter.query}
                   onChange={e => setFilter(f => ({ ...f, query: e.target.value }))}
                   onKeyDown={e => { if (e.key === 'Enter') fetchRows(); }} />
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="export-button" onClick={fetchRows} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
              {canCreate && (
                <button className="export-button" onClick={openCreate}>
                  <FaPlus /> Add entry
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No watchlist entries match the current filters.</p>
      )}

      {rows.map(r => (
        <div key={r.id} style={{
          background: 'var(--card-bg,#fff)',
          border: '1px solid var(--card-border,#e3e6ea)',
          borderLeft: `4px solid ${r.severity === 'critical' ? '#c62828'
                                  : r.severity === 'high'     ? '#ef6c00'
                                  : r.severity === 'medium'   ? '#0d47a1'
                                  :                             '#607d8b'}`,
          padding: '0.85rem 1.05rem',
          marginBottom: '0.5rem',
          opacity: r.is_active ? 1 : 0.65
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: 'var(--text-secondary)', fontWeight: 700 }}>
                {r.entry_type.replace(/_/g, ' ')}
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}><FaShieldAlt /> {r.value}</div>
              {r.reason && <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{r.reason}</div>}
              <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap', fontSize: '0.7rem' }}>
                <span className="tm-pill">{r.severity}</span>
                <span className="tm-pill">{r.is_active ? 'active' : 'inactive'}</span>
                {r.expires_at && <span className="tm-pill">Expires {fmt(r.expires_at)}</span>}
                <span className="tm-pill">Added {fmt(r.added_at)} {r.added_by_email ? `by ${r.added_by_email}` : ''}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
              {canToggle && (
                <button className="action-button" onClick={() => toggle(r)}
                        title={r.is_active ? 'Disable' : 'Enable'}>
                  {r.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                </button>
              )}
              {canUpdate && (
                <button className="action-button" onClick={() => openEdit(r)}
                        title="Edit">
                  <FaEdit />
                </button>
              )}
              {canDelete && (
                <button className="action-button delete" onClick={() => remove(r)}
                        title="Delete">
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Create / edit modal */}
      {editor && (
        <div className="modal-overlay" onClick={() => !busy && setEditor(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <button className="modal-close" onClick={() => !busy && setEditor(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>
              {editor.mode === 'create' ? 'Add watchlist entry' : 'Edit watchlist entry'}
            </h2>
            <div className="form-grid-2">
              <div className="form-group">
                <label>Entry type</label>
                <select value={editor.data.entry_type}
                        disabled={editor.mode === 'edit'}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, entry_type: e.target.value } }))}>
                  {ENTRY_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Severity</label>
                <select value={editor.data.severity}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, severity: e.target.value } }))}>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group form-group--full">
                <label>Value</label>
                <input type="text" maxLength="255"
                       value={editor.data.value}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, value: e.target.value } }))} />
              </div>
              <div className="form-group form-group--full">
                <label>Reason (short)</label>
                <input type="text" maxLength="255"
                       value={editor.data.reason}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, reason: e.target.value } }))}
                       placeholder="e.g. Confirmed fraud — case CASE-2026-0000017" />
              </div>
              <div className="form-group form-group--full">
                <label>Notes</label>
                <textarea rows={3}
                          value={editor.data.notes}
                          onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, notes: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Expires (optional)</label>
                <input type="date"
                       value={editor.data.expires_at}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, expires_at: e.target.value } }))} />
              </div>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" disabled={busy} onClick={() => setEditor(null)}>Cancel</button>
              <button className="export-button" disabled={busy} onClick={submit}>
                {busy ? 'Saving…' : (editor.mode === 'create' ? 'Add to watchlist' : 'Save changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageWatchlist;
